import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, Search, Filter, TrendingUp, TrendingDown, Calendar, DollarSign,
  Users, Building2, Landmark, ChevronDown, ChevronUp, ExternalLink, Bell,
  AlertTriangle, Star, ArrowUpRight, ArrowDownRight, BarChart3, PieChart,
  Zap, Eye, Download, RefreshCw, Info, ChevronRight, Award, Target,
  ChevronLeft, X, ArrowLeft, Check, Mail
} from 'lucide-react';
import './CongressPage.css';

// =============================================================================
// MOCK DATA - Replace with real API calls
// =============================================================================
const mockTrades = [
  { id: 1, politician: 'Nancy Pelosi', party: 'D', chamber: 'House', state: 'CA', stock: 'NVDA', company: 'NVIDIA Corp', type: 'Purchase', amount: '$1M - $5M', amountNum: 3000000, filed: '2026-01-28', traded: '2026-01-15', returnPct: 12.4, smartScore: 9, isOptions: true, committee: 'Financial Services' },
  { id: 2, politician: 'Dan Crenshaw', party: 'R', chamber: 'House', state: 'TX', stock: 'MSFT', company: 'Microsoft Corp', type: 'Purchase', amount: '$100K - $250K', amountNum: 175000, filed: '2026-01-27', traded: '2026-01-10', returnPct: 5.2, smartScore: 7, isOptions: false, committee: 'Energy' },
  { id: 3, politician: 'Tommy Tuberville', party: 'R', chamber: 'Senate', state: 'AL', stock: 'LMT', company: 'Lockheed Martin', type: 'Purchase', amount: '$250K - $500K', amountNum: 375000, filed: '2026-01-26', traded: '2026-01-08', returnPct: 8.7, smartScore: 8, isOptions: false, committee: 'Armed Services' },
  { id: 4, politician: 'Ro Khanna', party: 'D', chamber: 'House', state: 'CA', stock: 'GOOGL', company: 'Alphabet Inc', type: 'Sale', amount: '$500K - $1M', amountNum: 750000, filed: '2026-01-25', traded: '2026-01-05', returnPct: -3.1, smartScore: 5, isOptions: false, committee: 'Oversight' },
  { id: 5, politician: 'Marjorie Taylor Greene', party: 'R', chamber: 'House', state: 'GA', stock: 'DWAC', company: 'Digital World', type: 'Purchase', amount: '$15K - $50K', amountNum: 32500, filed: '2026-01-24', traded: '2026-01-03', returnPct: 24.6, smartScore: 6, isOptions: false, committee: 'Homeland Security' },
  { id: 6, politician: 'Mark Warner', party: 'D', chamber: 'Senate', state: 'VA', stock: 'CRM', company: 'Salesforce Inc', type: 'Purchase', amount: '$100K - $250K', amountNum: 175000, filed: '2026-01-23', traded: '2026-01-02', returnPct: 6.8, smartScore: 7, isOptions: false, committee: 'Intelligence' },
  { id: 7, politician: 'Josh Gottheimer', party: 'D', chamber: 'House', state: 'NJ', stock: 'AAPL', company: 'Apple Inc', type: 'Purchase', amount: '$250K - $500K', amountNum: 375000, filed: '2026-01-22', traded: '2025-12-28', returnPct: 4.2, smartScore: 6, isOptions: false, committee: 'Financial Services' },
  { id: 8, politician: 'Michael McCaul', party: 'R', chamber: 'House', state: 'TX', stock: 'RTX', company: 'RTX Corp', type: 'Purchase', amount: '$500K - $1M', amountNum: 750000, filed: '2026-01-21', traded: '2025-12-20', returnPct: 9.3, smartScore: 8, isOptions: false, committee: 'Foreign Affairs' },
  { id: 9, politician: 'David McCormick', party: 'R', chamber: 'Senate', state: 'PA', stock: 'GS', company: 'Goldman Sachs', type: 'Purchase', amount: '$1M - $5M', amountNum: 2500000, filed: '2026-01-20', traded: '2025-12-18', returnPct: 7.1, smartScore: 7, isOptions: false, committee: 'Banking' },
  { id: 10, politician: 'Katie Porter', party: 'D', chamber: 'House', state: 'CA', stock: 'AMZN', company: 'Amazon.com', type: 'Sale', amount: '$50K - $100K', amountNum: 75000, filed: '2026-01-19', traded: '2025-12-15', returnPct: -1.8, smartScore: 5, isOptions: false, committee: 'Oversight' },
  { id: 11, politician: 'Rick Scott', party: 'R', chamber: 'Senate', state: 'FL', stock: 'UNH', company: 'UnitedHealth', type: 'Purchase', amount: '$250K - $500K', amountNum: 375000, filed: '2026-01-18', traded: '2025-12-12', returnPct: 11.2, smartScore: 8, isOptions: false, committee: 'Finance' },
  { id: 12, politician: 'Alexandria Ocasio-Cortez', party: 'D', chamber: 'House', state: 'NY', stock: 'TSLA', company: 'Tesla Inc', type: 'Sale', amount: '$15K - $50K', amountNum: 32500, filed: '2026-01-17', traded: '2025-12-10', returnPct: -5.4, smartScore: 4, isOptions: false, committee: 'Financial Services' },
];

const topTraders = [
  { name: 'Nancy Pelosi', party: 'D', chamber: 'House', trades: 19, volume: '$51.7M', returnPct: 42.3, avatar: '👩‍💼' },
  { name: 'Michael McCaul', party: 'R', chamber: 'House', trades: 1013, volume: '$57.9M', returnPct: 28.7, avatar: '👨‍💼' },
  { name: 'Tommy Tuberville', party: 'R', chamber: 'Senate', trades: 26, volume: '$881K', returnPct: 31.5, avatar: '👨‍💼' },
  { name: 'Marjorie Taylor Greene', party: 'R', chamber: 'House', trades: 213, volume: '$1.9M', returnPct: 52.1, avatar: '👩‍💼' },
  { name: 'Ro Khanna', party: 'D', chamber: 'House', trades: 4130, volume: '$53.5M', returnPct: 18.2, avatar: '👨‍💼' },
];

const convergenceSignals = [
  { symbol: 'NVDA', name: 'NVIDIA Corp', signals: ['congress', 'insider', 'institution'], strength: 'high' },
  { symbol: 'LMT', name: 'Lockheed Martin', signals: ['congress', 'insider', 'institution'], strength: 'high' },
  { symbol: 'RTX', name: 'RTX Corp', signals: ['congress', 'institution'], strength: 'medium' },
  { symbol: 'MSFT', name: 'Microsoft Corp', signals: ['congress', 'insider'], strength: 'medium' },
  { symbol: 'AAPL', name: 'Apple Inc', signals: ['insider', 'institution'], strength: 'medium' },
];

const partyStats = {
  democrat: { buys: 847, sells: 312, netVolume: 234000000, topSector: 'Technology' },
  republican: { buys: 923, sells: 287, netVolume: 289000000, topSector: 'Defense' },
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatCurrency = (num) => {
  if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num}`;
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

// AI Insight Card (display only — chat handled by site-wide AIChatWidget)
function AIInsightCard({ insight }) {
  return (
    <div className="ai-insight-card">
      <div className="ai-insight-header">
        <div className="ai-icon"><Brain size={18} /></div>
        <h3>AI Market Intelligence</h3>
        <span className="ai-badge">Live Analysis</span>
      </div>
      <p className="ai-insight-text">{insight}</p>
    </div>
  );
}

// Stat Card
function StatCard({ icon: Icon, label, value, subValue, trend, color }) {
  return (
    <div className="stat-card">
      <div className="stat-icon" style={{ background: color }}>
        <Icon size={20} />
      </div>
      <div className="stat-content">
        <span className="stat-label">{label}</span>
        <span className="stat-value">{value}</span>
        {subValue && <span className={`stat-sub ${trend || ''}`}>{subValue}</span>}
      </div>
    </div>
  );
}

// Party Badge
function PartyBadge({ party, size = 'normal' }) {
  return (
    <span className={`party-badge ${party === 'D' ? 'democrat' : 'republican'} ${size}`}>
      {party}
    </span>
  );
}

// Smart Score Badge
function SmartScoreBadge({ score }) {
  const getClass = (s) => {
    if (s >= 8) return 'high';
    if (s >= 5) return 'medium';
    return 'low';
  };
  return (
    <div className={`smart-score ${getClass(score)}`}>
      <Zap size={12} />
      {score}
    </div>
  );
}

// Type Badge
function TypeBadge({ type }) {
  const isPurchase = type.toLowerCase() === 'purchase';
  return (
    <span className={`type-badge ${isPurchase ? 'purchase' : 'sale'}`}>
      {isPurchase ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
      {type}
    </span>
  );
}

// Trade Row
function TradeRow({ trade, onViewStock, onViewPolitician }) {
  const handleRowClick = (e) => {
    if (e.target.closest('.row-action-btn')) {
      return;
    }
    onViewPolitician?.(trade.politician);
  };

  return (
    <tr className="trade-row" onClick={handleRowClick}>
      <td className="politician-cell">
        <div className="politician-info">
          <PartyBadge party={trade.party} />
          <div className="politician-details">
            <span className="politician-name">{trade.politician}</span>
            <span className="politician-meta">{trade.chamber} • {trade.state}</span>
          </div>
        </div>
      </td>
      <td className="stock-cell">
        <div className="stock-info">
          <span className="stock-symbol">{trade.stock}</span>
          <span className="stock-name">{trade.company}</span>
          {trade.isOptions && <span className="options-badge">OPTIONS</span>}
        </div>
      </td>
      <td><TypeBadge type={trade.type} /></td>
      <td className="amount-cell"><span>{trade.amount}</span></td>
      <td className="date-cell">
        <div className="date-info">
          <span className="filed-date">{formatDate(trade.filed)}</span>
          <span className="traded-date">Traded: {formatDate(trade.traded)}</span>
        </div>
      </td>
      <td className={`return-cell ${trade.returnPct >= 0 ? 'positive' : 'negative'}`}>
        <span>{trade.returnPct >= 0 ? '+' : ''}{trade.returnPct}%</span>
      </td>
      <td><SmartScoreBadge score={trade.smartScore} /></td>
      <td>
        <button 
          className="row-action-btn" 
          onClick={(e) => { e.stopPropagation(); onViewStock?.(trade.stock); }}
          title={`View ${trade.stock} Insider Activity`}
        >
          <Eye size={16} />
        </button>
      </td>
    </tr>
  );
}

// Leaderboard Row
function LeaderboardRow({ trader, rank, onClick }) {
  const getRankClass = (r) => {
    if (r === 1) return 'gold';
    if (r === 2) return 'silver';
    if (r === 3) return 'bronze';
    return '';
  };

  return (
    <div className="leaderboard-row" onClick={() => onClick?.(trader.name)}>
      <span className={`rank ${getRankClass(rank)}`}>{rank}</span>
      <div className="trader-info">
        <span className="trader-avatar">{trader.avatar}</span>
        <div className="trader-details">
          <span className="trader-name">
            {trader.name.length > 15 ? trader.name.substring(0, 15) + '...' : trader.name}
            <PartyBadge party={trader.party} size="small" />
          </span>
          <span className="trader-meta">{trader.trades} trades • {trader.volume}</span>
        </div>
      </div>
      <span className={`trader-return ${trader.returnPct >= 0 ? 'positive' : 'negative'}`}>
        +{trader.returnPct}%
      </span>
    </div>
  );
}

// Convergence Signal Item
function ConvergenceItem({ signal, onClick }) {
  return (
    <div className="convergence-item" onClick={() => onClick?.(signal.symbol)}>
      <div className="conv-stock">
        <span className="conv-symbol">{signal.symbol}</span>
        <span className="conv-name">{signal.name}</span>
      </div>
      <div className="conv-signals">
        {signal.signals.includes('congress') && <span className="signal congress" title="Congress Buying">C</span>}
        {signal.signals.includes('insider') && <span className="signal insider" title="Insider Buying">I</span>}
        {signal.signals.includes('institution') && <span className="signal institution" title="Institutional Buying">F</span>}
      </div>
      <span className={`conv-strength ${signal.strength}`}>
        {signal.strength === 'high' ? 'Strong' : 'Moderate'}
      </span>
    </div>
  );
}

// =============================================================================
// ALERT MODAL COMPONENT
// =============================================================================
function AlertModal({ isOpen, onClose }) {
  const [alertSettings, setAlertSettings] = useState({
    newTrades: true, largeTrades: true, largeTradeThreshold: '1000000',
    allPoliticians: true, selectedPoliticians: [], allStocks: true, selectedStocks: [],
    allSectors: true, selectedSectors: [], convergenceSignals: true,
    emailAlerts: true, smsAlerts: false, email: ''
  });
  const [politicianSearch, setPoliticianSearch] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [activeTab, setActiveTab] = useState('alerts');
  const [saved, setSaved] = useState(false);

  const allPoliticians = [
    { name: 'Nancy Pelosi', party: 'D', chamber: 'House', trades: 19, volume: '$51.7M', returnPct: 42.3, popular: true },
    { name: 'Michael McCaul', party: 'R', chamber: 'House', trades: 1013, volume: '$57.9M', returnPct: 28.7, popular: true },
    { name: 'Tommy Tuberville', party: 'R', chamber: 'Senate', trades: 26, volume: '$881K', returnPct: 31.5, popular: true },
    { name: 'Marjorie Taylor Greene', party: 'R', chamber: 'House', trades: 213, volume: '$1.9M', returnPct: 52.1, popular: true },
    { name: 'Ro Khanna', party: 'D', chamber: 'House', trades: 4130, volume: '$53.5M', returnPct: 18.2, popular: true },
    { name: 'Mark Warner', party: 'D', chamber: 'Senate', trades: 45, volume: '$2.1M', returnPct: 15.8, popular: false },
    { name: 'Dan Crenshaw', party: 'R', chamber: 'House', trades: 89, volume: '$1.2M', returnPct: 22.4, popular: false },
    { name: 'Josh Gottheimer', party: 'D', chamber: 'House', trades: 156, volume: '$3.4M', returnPct: 19.2, popular: false },
    { name: 'Rick Scott', party: 'R', chamber: 'Senate', trades: 67, volume: '$4.8M', returnPct: 25.1, popular: false },
    { name: 'Katie Porter', party: 'D', chamber: 'House', trades: 34, volume: '$890K', returnPct: 12.3, popular: false },
    { name: 'Alexandria Ocasio-Cortez', party: 'D', chamber: 'House', trades: 12, volume: '$120K', returnPct: 8.5, popular: false },
    { name: 'David McCormick', party: 'R', chamber: 'Senate', trades: 78, volume: '$6.2M', returnPct: 21.7, popular: false },
  ];

  const allStocks = [
    { symbol: 'NVDA', name: 'NVIDIA Corp', trades: 24, volume: '$18.2M', sector: 'Technology' },
    { symbol: 'MSFT', name: 'Microsoft Corp', trades: 18, volume: '$12.4M', sector: 'Technology' },
    { symbol: 'AAPL', name: 'Apple Inc', trades: 15, volume: '$8.9M', sector: 'Technology' },
    { symbol: 'LMT', name: 'Lockheed Martin', trades: 12, volume: '$7.2M', sector: 'Defense' },
    { symbol: 'RTX', name: 'RTX Corp', trades: 10, volume: '$5.8M', sector: 'Defense' },
    { symbol: 'GOOGL', name: 'Alphabet Inc', trades: 14, volume: '$9.1M', sector: 'Technology' },
    { symbol: 'AMZN', name: 'Amazon.com', trades: 11, volume: '$6.3M', sector: 'Consumer' },
    { symbol: 'UNH', name: 'UnitedHealth', trades: 8, volume: '$4.5M', sector: 'Healthcare' },
    { symbol: 'JPM', name: 'JPMorgan Chase', trades: 9, volume: '$5.1M', sector: 'Financials' },
    { symbol: 'XOM', name: 'Exxon Mobil', trades: 7, volume: '$3.8M', sector: 'Energy' },
  ];

  const allSectors = [
    { name: 'Technology', volume: '$165M', trades: 89, color: '#8b5cf6' },
    { name: 'Defense', volume: '$101M', trades: 45, color: '#3b82f6' },
    { name: 'Healthcare', volume: '$90M', trades: 38, color: '#10b981' },
    { name: 'Financials', volume: '$94M', trades: 42, color: '#f59e0b' },
    { name: 'Energy', volume: '$43M', trades: 21, color: '#ef4444' },
    { name: 'Consumer', volume: '$40M', trades: 19, color: '#ec4899' },
  ];

  const filteredPoliticians = politicianSearch.trim() 
    ? allPoliticians.filter(p => p.name.toLowerCase().includes(politicianSearch.toLowerCase()) || p.party.toLowerCase() === politicianSearch.toLowerCase() || p.chamber.toLowerCase().includes(politicianSearch.toLowerCase()))
    : allPoliticians.filter(p => p.popular);

  const filteredStocks = stockSearch.trim()
    ? allStocks.filter(s => s.symbol.toLowerCase().includes(stockSearch.toLowerCase()) || s.name.toLowerCase().includes(stockSearch.toLowerCase()) || s.sector.toLowerCase().includes(stockSearch.toLowerCase()))
    : allStocks.slice(0, 6);

  const togglePolitician = (name) => {
    if (alertSettings.selectedPoliticians.includes(name)) {
      setAlertSettings({...alertSettings, selectedPoliticians: alertSettings.selectedPoliticians.filter(x => x !== name)});
    } else {
      setAlertSettings({...alertSettings, allPoliticians: false, selectedPoliticians: [...alertSettings.selectedPoliticians, name]});
    }
  };

  const toggleStock = (symbol) => {
    if (alertSettings.selectedStocks.includes(symbol)) {
      setAlertSettings({...alertSettings, selectedStocks: alertSettings.selectedStocks.filter(x => x !== symbol)});
    } else {
      setAlertSettings({...alertSettings, allStocks: false, selectedStocks: [...alertSettings.selectedStocks, symbol]});
    }
  };

  const toggleSector = (name) => {
    if (alertSettings.selectedSectors.includes(name)) {
      setAlertSettings({...alertSettings, selectedSectors: alertSettings.selectedSectors.filter(x => x !== name)});
    } else {
      setAlertSettings({...alertSettings, allSectors: false, selectedSectors: [...alertSettings.selectedSectors, name]});
    }
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content alert-modal congress-alert-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Bell size={20} /> Congress Trade Alerts</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="alert-tabs">
          <button className={`alert-tab ${activeTab === 'alerts' ? 'active' : ''}`} onClick={() => setActiveTab('alerts')}><Bell size={16} /> Alert Types</button>
          <button className={`alert-tab ${activeTab === 'politicians' ? 'active' : ''}`} onClick={() => setActiveTab('politicians')}>
            <Users size={16} /> Politicians
            {!alertSettings.allPoliticians && alertSettings.selectedPoliticians.length > 0 && <span className="tab-badge">{alertSettings.selectedPoliticians.length}</span>}
          </button>
          <button className={`alert-tab ${activeTab === 'stocks' ? 'active' : ''}`} onClick={() => setActiveTab('stocks')}>
            <TrendingUp size={16} /> Stocks & Sectors
            {(!alertSettings.allStocks && alertSettings.selectedStocks.length > 0) || (!alertSettings.allSectors && alertSettings.selectedSectors.length > 0) ? <span className="tab-badge">{alertSettings.selectedStocks.length + alertSettings.selectedSectors.length}</span> : null}
          </button>
          <button className={`alert-tab ${activeTab === 'delivery' ? 'active' : ''}`} onClick={() => setActiveTab('delivery')}><Mail size={16} /> Delivery</button>
        </div>
        
        <div className="modal-body">
          {activeTab === 'alerts' && (
            <div className="alert-section">
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.newTrades} onChange={e => setAlertSettings({...alertSettings, newTrades: e.target.checked})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">All New Trades</span><span className="option-desc">Get notified when any new trade is filed</span></div>
              </label>
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.largeTrades} onChange={e => setAlertSettings({...alertSettings, largeTrades: e.target.checked})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">Large Trades Only</span><span className="option-desc">Trades over threshold amount</span></div>
              </label>
              {alertSettings.largeTrades && (
                <div className="sub-option">
                  <label>Minimum Amount</label>
                  <select value={alertSettings.largeTradeThreshold} onChange={e => setAlertSettings({...alertSettings, largeTradeThreshold: e.target.value})}>
                    <option value="100000">$100K+</option><option value="250000">$250K+</option><option value="500000">$500K+</option><option value="1000000">$1M+</option><option value="5000000">$5M+</option>
                  </select>
                </div>
              )}
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.convergenceSignals} onChange={e => setAlertSettings({...alertSettings, convergenceSignals: e.target.checked})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">Convergence Signals</span><span className="option-desc">When Congress + Insider + Institutional align</span></div>
              </label>
            </div>
          )}

          {activeTab === 'politicians' && (
            <div className="alert-section">
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.allPoliticians} onChange={e => setAlertSettings({...alertSettings, allPoliticians: e.target.checked, selectedPoliticians: e.target.checked ? [] : alertSettings.selectedPoliticians})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">All Politicians</span><span className="option-desc">Get alerts for any congressional member</span></div>
              </label>
              <div className="filter-divider"><span>or select specific politicians</span></div>
              {alertSettings.selectedPoliticians.length > 0 && (
                <div className="selected-politicians">
                  {alertSettings.selectedPoliticians.map(name => {
                    const p = allPoliticians.find(x => x.name === name);
                    return (<div key={name} className="selected-chip"><span className={`party-dot ${p?.party === 'D' ? 'dem' : 'rep'}`}></span><span>{name}</span><button onClick={() => togglePolitician(name)}><X size={14} /></button></div>);
                  })}
                </div>
              )}
              <div className="politician-search"><Search size={16} /><input type="text" placeholder="Search politicians..." value={politicianSearch} onChange={e => setPoliticianSearch(e.target.value)} />{politicianSearch && <button className="clear-search" onClick={() => setPoliticianSearch('')}><X size={14} /></button>}</div>
              <div className="suggestions-label">{politicianSearch ? 'Search Results' : '🔥 Top Traders'}</div>
              <div className="politician-list">
                {filteredPoliticians.map(p => (
                  <div key={p.name} className={`politician-item ${alertSettings.selectedPoliticians.includes(p.name) ? 'selected' : ''}`} onClick={() => togglePolitician(p.name)}>
                    <div className="politician-item-left"><span className={`party-badge ${p.party === 'D' ? 'dem' : 'rep'}`}>{p.party}</span><div className="politician-item-info"><span className="politician-item-name">{p.name}</span><span className="politician-item-meta">{p.chamber} • {p.trades} trades</span></div></div>
                    <div className="politician-item-right"><span className={`politician-item-return ${p.returnPct >= 0 ? 'positive' : 'negative'}`}>+{p.returnPct}%</span>{alertSettings.selectedPoliticians.includes(p.name) && <Check size={16} className="check-icon" />}</div>
                  </div>
                ))}
                {filteredPoliticians.length === 0 && <div className="no-results">No politicians found</div>}
              </div>
            </div>
          )}

          {activeTab === 'stocks' && (
            <div className="alert-section">
              <h4>Sectors</h4>
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.allSectors} onChange={e => setAlertSettings({...alertSettings, allSectors: e.target.checked, selectedSectors: e.target.checked ? [] : alertSettings.selectedSectors})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">All Sectors</span><span className="option-desc">Get alerts across all market sectors</span></div>
              </label>
              <div className="sector-chips">
                {allSectors.map(s => (
                  <button key={s.name} className={`sector-chip ${alertSettings.selectedSectors.includes(s.name) ? 'selected' : ''}`} onClick={() => toggleSector(s.name)} style={{ '--sector-color': s.color }}>
                    <span className="sector-dot" style={{ background: s.color }}></span>{s.name}{alertSettings.selectedSectors.includes(s.name) && <Check size={14} />}
                  </button>
                ))}
              </div>
              <h4 style={{ marginTop: '1.5rem' }}>Specific Stocks</h4>
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.allStocks} onChange={e => setAlertSettings({...alertSettings, allStocks: e.target.checked, selectedStocks: e.target.checked ? [] : alertSettings.selectedStocks})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">All Stocks</span><span className="option-desc">Get alerts for any stock traded</span></div>
              </label>
              {alertSettings.selectedStocks.length > 0 && (
                <div className="selected-politicians">{alertSettings.selectedStocks.map(symbol => (<div key={symbol} className="selected-chip stock-chip"><span>{symbol}</span><button onClick={() => toggleStock(symbol)}><X size={14} /></button></div>))}</div>
              )}
              <div className="politician-search"><Search size={16} /><input type="text" placeholder="Search stocks..." value={stockSearch} onChange={e => setStockSearch(e.target.value)} />{stockSearch && <button className="clear-search" onClick={() => setStockSearch('')}><X size={14} /></button>}</div>
              <div className="suggestions-label">{stockSearch ? 'Search Results' : '🔥 Most Traded'}</div>
              <div className="politician-list stock-list">
                {filteredStocks.map(s => (
                  <div key={s.symbol} className={`politician-item stock-item ${alertSettings.selectedStocks.includes(s.symbol) ? 'selected' : ''}`} onClick={() => toggleStock(s.symbol)}>
                    <div className="politician-item-left"><span className="stock-symbol-badge">{s.symbol}</span><div className="politician-item-info"><span className="politician-item-name">{s.name}</span><span className="politician-item-meta">{s.sector} • {s.trades} trades</span></div></div>
                    <div className="politician-item-right"><span className="stock-volume">{s.volume}</span>{alertSettings.selectedStocks.includes(s.symbol) && <Check size={16} className="check-icon" />}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'delivery' && (
            <div className="alert-section">
              <h4>How should we notify you?</h4>
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.emailAlerts} onChange={e => setAlertSettings({...alertSettings, emailAlerts: e.target.checked})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">Email Notifications</span><span className="option-desc">Detailed alerts with full trade info</span></div>
              </label>
              {alertSettings.emailAlerts && <div className="sub-option"><input type="email" placeholder="Enter your email" value={alertSettings.email} onChange={e => setAlertSettings({...alertSettings, email: e.target.value})} /></div>}
              <label className="alert-option">
                <input type="checkbox" checked={alertSettings.smsAlerts} onChange={e => setAlertSettings({...alertSettings, smsAlerts: e.target.checked})} />
                <span className="checkbox-custom"></span>
                <div className="option-text"><span className="option-label">SMS Notifications</span><span className="option-desc">Quick text alerts for urgent trades</span></div>
              </label>
              <div className="delivery-note"><Info size={14} /><span>You can manage notification preferences anytime in Settings</span></div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>Cancel</button>
          <button className="modal-btn primary" onClick={handleSave}>{saved ? <><Check size={16} /> Saved!</> : <><Bell size={16} /> Enable Alerts</>}</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// EXPORT MODAL COMPONENT
// =============================================================================
function ExportModal({ isOpen, onClose, trades }) {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportRange, setExportRange] = useState('filtered');
  const [includeFields, setIncludeFields] = useState({ politician: true, party: true, stock: true, type: true, amount: true, filed: true, traded: true, return: true, score: true });
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      const headers = [];
      if (includeFields.politician) headers.push('Politician');
      if (includeFields.party) headers.push('Party');
      if (includeFields.stock) headers.push('Stock');
      if (includeFields.type) headers.push('Type');
      if (includeFields.amount) headers.push('Amount');
      if (includeFields.filed) headers.push('Filed Date');
      if (includeFields.traded) headers.push('Traded Date');
      if (includeFields.return) headers.push('Return %');
      if (includeFields.score) headers.push('Smart Score');
      const dataToExport = exportRange === 'all' ? mockTrades : trades;
      const rows = dataToExport.map(trade => {
        const row = [];
        if (includeFields.politician) row.push(trade.politician);
        if (includeFields.party) row.push(trade.party);
        if (includeFields.stock) row.push(trade.stock);
        if (includeFields.type) row.push(trade.type);
        if (includeFields.amount) row.push(trade.amount);
        if (includeFields.filed) row.push(trade.filed);
        if (includeFields.traded) row.push(trade.traded);
        if (includeFields.return) row.push(trade.returnPct);
        if (includeFields.score) row.push(trade.smartScore);
        return row.join(',');
      });
      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `congress-trades-${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExporting(false);
      setExported(true);
      setTimeout(() => { setExported(false); onClose(); }, 1500);
    }, 1000);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content export-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Download size={20} /> Export Data</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="export-section">
            <h4>Format</h4>
            <div className="format-options">
              <label className={`format-option ${exportFormat === 'csv' ? 'selected' : ''}`}><input type="radio" name="format" value="csv" checked={exportFormat === 'csv'} onChange={e => setExportFormat(e.target.value)} /><div className="format-icon">📊</div><span>CSV</span></label>
              <label className={`format-option ${exportFormat === 'json' ? 'selected' : ''}`}><input type="radio" name="format" value="json" checked={exportFormat === 'json'} onChange={e => setExportFormat(e.target.value)} /><div className="format-icon">{ }</div><span>JSON</span></label>
              <label className={`format-option ${exportFormat === 'xlsx' ? 'selected' : ''}`}><input type="radio" name="format" value="xlsx" checked={exportFormat === 'xlsx'} onChange={e => setExportFormat(e.target.value)} /><div className="format-icon">📗</div><span>Excel</span></label>
            </div>
          </div>
          <div className="export-section">
            <h4>Data Range</h4>
            <div className="range-options">
              <label className="alert-option"><input type="radio" name="range" value="filtered" checked={exportRange === 'filtered'} onChange={e => setExportRange(e.target.value)} /><span className="radio-custom"></span><div className="option-text"><span className="option-label">Current Filtered View</span><span className="option-desc">{trades.length} trades</span></div></label>
              <label className="alert-option"><input type="radio" name="range" value="all" checked={exportRange === 'all'} onChange={e => setExportRange(e.target.value)} /><span className="radio-custom"></span><div className="option-text"><span className="option-label">All Data</span><span className="option-desc">{mockTrades.length} trades</span></div></label>
            </div>
          </div>
          <div className="export-section">
            <h4>Include Fields</h4>
            <div className="field-options">
              {Object.entries(includeFields).map(([field, checked]) => (<label key={field} className="field-chip"><input type="checkbox" checked={checked} onChange={e => setIncludeFields({...includeFields, [field]: e.target.checked})} /><span>{field.charAt(0).toUpperCase() + field.slice(1)}</span></label>))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-btn secondary" onClick={onClose}>Cancel</button>
          <button className="modal-btn primary" onClick={handleExport} disabled={exporting}>{exported ? <><Check size={16} /> Downloaded!</> : exporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN CONGRESS PAGE COMPONENT
// =============================================================================
function CongressPage() {
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterParty, setFilterParty] = useState('all');
  const [filterChamber, setFilterChamber] = useState('all');
  const [filterCommittee, setFilterCommittee] = useState('all');
  const [sortBy, setSortBy] = useState('filed');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const tradesPerPage = 10;

  const filteredTrades = useMemo(() => {
    return mockTrades
      .filter(trade => {
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          if (!trade.politician.toLowerCase().includes(query) && !trade.stock.toLowerCase().includes(query) && !trade.company.toLowerCase().includes(query)) return false;
        }
        if (filterType !== 'all' && trade.type.toLowerCase() !== filterType) return false;
        if (filterParty !== 'all' && trade.party !== filterParty) return false;
        if (filterChamber !== 'all' && trade.chamber.toLowerCase() !== filterChamber) return false;
        if (filterCommittee !== 'all' && trade.committee !== filterCommittee) return false;
        return true;
      })
      .sort((a, b) => {
        const order = sortOrder === 'desc' ? -1 : 1;
        switch (sortBy) {
          case 'filed': return order * (new Date(b.filed) - new Date(a.filed));
          case 'amount': return order * (b.amountNum - a.amountNum);
          case 'return': return order * (b.returnPct - a.returnPct);
          case 'score': return order * (b.smartScore - a.smartScore);
          default: return 0;
        }
      });
  }, [searchQuery, filterType, filterParty, filterChamber, filterCommittee, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredTrades.length / tradesPerPage);
  const paginatedTrades = filteredTrades.slice((currentPage - 1) * tradesPerPage, currentPage * tradesPerPage);

  const handleViewStock = (symbol) => navigate(`/stock/${symbol}?tab=government`);
  const handleViewPolitician = (name) => {
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    navigate(`/politician/${slug}`);
  };
  const handleExport = () => setShowExportModal(true);
  const handleSetAlert = () => setShowAlertModal(true);

  const aiInsight = `Congressional trading activity surged 34% this week with strong buying in Technology and Defense sectors. Notable: Nancy Pelosi filed $3M in NVDA call options ahead of the AI infrastructure bill vote. Cross-referencing with insider data shows 3 convergence signals on NVDA, LMT, and RTX. Republican members showing increased Defense sector allocation (+18% MoM) while Democrats concentrate on Tech (+12% MoM).`;

  return (
    <div className="congress-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <Landmark size={28} className="header-icon" />
            <div><h1>Congress Trading</h1><p>Track stock trades by U.S. Congress members • STOCK Act Disclosures</p></div>
          </div>
          <div className="header-actions">
            <button className="header-btn" onClick={handleExport}><Download size={16} /> Export</button>
            <button className="header-btn primary" onClick={handleSetAlert}><Bell size={16} /> Set Alerts</button>
          </div>
        </div>
      </div>

      <AIInsightCard insight={aiInsight} />

      <div className="stats-row">
        <StatCard icon={BarChart3} label="Total Trades (30d)" value="1,847" subValue="+23% vs last month" trend="positive" color="linear-gradient(135deg, #667eea, #764ba2)" />
        <StatCard icon={TrendingUp} label="Net Buy Volume" value="$523M" subValue="72% buy ratio" trend="positive" color="linear-gradient(135deg, #10b981, #059669)" />
        <StatCard icon={Target} label="Avg Return (Post-Trade)" value="+8.4%" subValue="vs S&P +2.1%" trend="positive" color="linear-gradient(135deg, #f59e0b, #d97706)" />
        <StatCard icon={AlertTriangle} label="Convergence Signals" value="7" subValue="Congress + Insider + Inst." color="linear-gradient(135deg, #ef4444, #dc2626)" />
      </div>

      <div className="strategy-section">
        <div className="strategy-chart-container">
          <div className="strategy-header"><h2>Congress Long-Short Strategy</h2><div className="strategy-timeframes"><button className="tf-btn">1M</button><button className="tf-btn">3M</button><button className="tf-btn">6M</button><button className="tf-btn">YTD</button><button className="tf-btn">1Y</button><button className="tf-btn active">MAX</button></div></div>
          <div className="strategy-chart">
            <div className="strategy-return-badge"><span className="return-value positive">+383.74%</span><span className="return-label">All Time</span></div>
            <svg viewBox="0 0 800 200" preserveAspectRatio="none" className="strategy-svg">
              <defs><linearGradient id="strategyGradient" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#10b981" stopOpacity="0.3"/><stop offset="100%" stopColor="#10b981" stopOpacity="0"/></linearGradient></defs>
              <path d="M0,180 C50,175 100,170 150,160 C200,150 250,140 300,120 C350,100 400,90 450,70 C500,55 550,45 600,35 C650,28 700,22 750,18 L800,15" fill="none" stroke="#10b981" strokeWidth="2.5"/>
              <path d="M0,180 C50,175 100,170 150,160 C200,150 250,140 300,120 C350,100 400,90 450,70 C500,55 550,45 600,35 C650,28 700,22 750,18 L800,15 L800,200 L0,200 Z" fill="url(#strategyGradient)"/>
            </svg>
            <div className="chart-x-axis"><span>2020</span><span>2021</span><span>2022</span><span>2023</span><span>2024</span><span>2025</span></div>
          </div>
        </div>
        <div className="strategy-about">
          <h3>About</h3>
          <p>The Congress Long-Short Strategy tracks the performance of stocks that have been purchased or sold by members of U.S. Congress (or their family). It takes a long position in stocks that have been purchased, and a short position in stocks which have been sold.</p>
          <div className="strategy-metrics">
            <div className="metric"><span className="metric-label">Backtest Start</span><span className="metric-value">2020-04-01</span></div>
            <div className="metric"><span className="metric-label">CAGR (Total)</span><span className="metric-value positive">30.95%</span></div>
            <div className="metric"><span className="metric-label">Return (30d)</span><span className="metric-value negative">-2.29%</span></div>
            <div className="metric"><span className="metric-label">Return (1Y)</span><span className="metric-value positive">26.38%</span></div>
          </div>
          <div className="copy-trade-wrapper">
            <button className="copy-trade-btn" onClick={() => setShowAlertModal(true)}><Bell size={18} /><span>Set Trade Alerts</span></button>
            <div className="copy-trade-info"><Info size={14} /><span>Automatically mirror congressional trades in your brokerage account when they're filed.</span></div>
          </div>
        </div>
      </div>

      <div className="search-filters-section">
        <div className="trades-header">
          <div className="search-box"><Search size={18} /><input type="text" placeholder="Search politician or stock..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />{searchQuery && <button className="clear-search" onClick={() => setSearchQuery('')}><X size={14} /></button>}</div>
          <div className="filter-toggles">
            <button className={`filter-btn ${filterType === 'all' ? 'active' : ''}`} onClick={() => setFilterType('all')}>All</button>
            <button className={`filter-btn buy ${filterType === 'purchase' ? 'active' : ''}`} onClick={() => setFilterType('purchase')}>Buys</button>
            <button className={`filter-btn sell ${filterType === 'sale' ? 'active' : ''}`} onClick={() => setFilterType('sale')}>Sells</button>
          </div>
          <button className={`advanced-filter-btn ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}><Filter size={16} />Filters<ChevronDown size={14} className={showFilters ? 'rotated' : ''} /></button>
        </div>

        {showFilters && (
          <div className="advanced-filters">
            <div className="filter-group"><label>Party</label><select value={filterParty} onChange={(e) => setFilterParty(e.target.value)}><option value="all">All Parties</option><option value="D">Democrat</option><option value="R">Republican</option></select></div>
            <div className="filter-group"><label>Chamber</label><select value={filterChamber} onChange={(e) => setFilterChamber(e.target.value)}><option value="all">All Chambers</option><option value="house">House</option><option value="senate">Senate</option></select></div>
            <div className="filter-group"><label>Sort By</label><select value={sortBy} onChange={(e) => setSortBy(e.target.value)}><option value="filed">Date Filed</option><option value="amount">Amount</option><option value="return">Return</option><option value="score">Smart Score</option></select></div>
            <div className="filter-group"><label>Committee</label><select value={filterCommittee} onChange={(e) => setFilterCommittee(e.target.value)}><option value="all">All Committees</option><option value="Financial Services">Financial Services</option><option value="Armed Services">Armed Services</option><option value="Intelligence">Intelligence</option><option value="Energy">Energy</option><option value="Foreign Affairs">Foreign Affairs</option></select></div>
          </div>
        )}

        <div className="recent-trades-label"><h3><Landmark size={18} /> Recent Trades - U.S. Congress</h3><span className="trades-count">{filteredTrades.length} trades</span></div>

        <div className="trades-table-container">
          <table className="trades-table">
            <thead><tr><th>Politician</th><th>Stock</th><th>Type</th><th>Amount</th><th>Filed</th><th>Return</th><th>Score</th><th></th></tr></thead>
            <tbody>
              {paginatedTrades.map(trade => (<TradeRow key={trade.id} trade={trade} onViewStock={handleViewStock} onViewPolitician={handleViewPolitician} />))}
              {paginatedTrades.length === 0 && (<tr><td colSpan="8" className="empty-state"><Search size={32} /><p>No trades found matching your filters</p></td></tr>)}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <span className="pagination-info">Showing {((currentPage - 1) * tradesPerPage) + 1}-{Math.min(currentPage * tradesPerPage, filteredTrades.length)} of {filteredTrades.length} trades</span>
          <div className="pagination-controls">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronLeft size={14} /> Prev</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => { const pageNum = i + 1; return (<button key={pageNum} className={currentPage === pageNum ? 'active' : ''} onClick={() => setCurrentPage(pageNum)}>{pageNum}</button>); })}
            {totalPages > 5 && <span>...</span>}
            {totalPages > 5 && <button onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next <ChevronRight size={14} /></button>
          </div>
        </div>
      </div>

      <div className="sector-breakdown-section">
        <div className="sector-card bar-chart-card">
          <div className="sector-card-header"><h3><PieChart size={20} /> Sector Activity (Buy vs Sell)</h3><span className="time-badge">30d</span></div>
          <div className="sector-bars">
            {[
              { sector: 'Technology', buy: 120, sell: 45, color: '#8b5cf6' },
              { sector: 'Defense', buy: 89, sell: 12, color: '#3b82f6' },
              { sector: 'Healthcare', buy: 56, sell: 34, color: '#10b981' },
              { sector: 'Financials', buy: 43, sell: 51, color: '#f59e0b' },
              { sector: 'Energy', buy: 28, sell: 15, color: '#ef4444' },
              { sector: 'Consumer', buy: 22, sell: 18, color: '#ec4899' },
            ].map(s => (
              <div key={s.sector} className="sector-bar-row">
                <div className="sector-bar-label"><span className="sector-name">{s.sector}</span><span className={`sector-net ${s.buy > s.sell ? 'positive' : 'negative'}`}>Net: {s.buy > s.sell ? '+' : '-'}${Math.abs(s.buy - s.sell)}M</span></div>
                <div className="sector-bar-track"><div className="sector-bar buy" style={{ width: `${(s.buy / 120) * 100}%` }}><span>${s.buy}M</span></div><div className="sector-bar sell" style={{ width: `${(s.sell / 120) * 100}%` }}><span>${s.sell}M</span></div></div>
              </div>
            ))}
          </div>
          <div className="sector-legend"><div className="legend-item"><span className="legend-dot buy"></span><span>Buys</span></div><div className="legend-item"><span className="legend-dot sell"></span><span>Sells</span></div></div>
        </div>

        <div className="sector-card donut-chart-card">
          <div className="sector-card-header"><h3><Target size={20} /> Volume by Sector</h3><span className="time-badge">30d</span></div>
          <div className="donut-container">
            <div className="donut-chart">
              <svg viewBox="0 0 100 100">
                {(() => {
                  const sectors = [{ sector: 'Technology', volume: 165, color: '#8b5cf6' },{ sector: 'Defense', volume: 101, color: '#3b82f6' },{ sector: 'Healthcare', volume: 90, color: '#10b981' },{ sector: 'Financials', volume: 94, color: '#f59e0b' },{ sector: 'Energy', volume: 43, color: '#ef4444' },{ sector: 'Consumer', volume: 40, color: '#ec4899' }];
                  const total = sectors.reduce((acc, s) => acc + s.volume, 0);
                  let cumulative = 0;
                  return sectors.map((s, i) => {
                    const percent = (s.volume / total) * 100;
                    const circumference = 2 * Math.PI * 35;
                    const strokeDash = (percent / 100) * circumference;
                    const strokeOffset = -(cumulative / 100) * circumference;
                    cumulative += percent;
                    return (<circle key={s.sector} cx="50" cy="50" r="35" fill="none" stroke={s.color} strokeWidth="12" strokeDasharray={`${strokeDash} ${circumference}`} strokeDashoffset={strokeOffset} style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }} />);
                  });
                })()}
              </svg>
              <div className="donut-center"><span className="donut-total">$533M</span><span className="donut-label">Total Volume</span></div>
            </div>
            <div className="donut-legend">
              {[{ sector: 'Technology', volume: 165, color: '#8b5cf6' },{ sector: 'Defense', volume: 101, color: '#3b82f6' },{ sector: 'Healthcare', volume: 90, color: '#10b981' },{ sector: 'Financials', volume: 94, color: '#f59e0b' },{ sector: 'Energy', volume: 43, color: '#ef4444' },{ sector: 'Consumer', volume: 40, color: '#ec4899' }].map(s => (
                <div key={s.sector} className="donut-legend-item"><span className="legend-color" style={{ background: s.color }}></span><span className="legend-sector">{s.sector}</span><span className="legend-percent">{((s.volume / 533) * 100).toFixed(1)}%</span></div>
              ))}
            </div>
          </div>
          <div className="hot-stocks-section">
            <div className="hot-stocks-header"><Zap size={14} /><span>Hot Stocks This Week</span></div>
            <div className="hot-stocks-list">
              {[{ symbol: 'NVDA', name: 'NVIDIA', volume: '$12.4M', traders: 8, change: '+18.2%' },{ symbol: 'MSFT', name: 'Microsoft', volume: '$8.7M', traders: 6, change: '+5.1%' },{ symbol: 'LMT', name: 'Lockheed', volume: '$6.2M', traders: 5, change: '+9.8%' }].map((stock, i) => (
                <div key={stock.symbol} className="hot-stock-item" onClick={() => navigate(`/stock/${stock.symbol}`)}>
                  <span className="hot-rank">{i + 1}</span>
                  <div className="hot-stock-info"><span className="hot-symbol">{stock.symbol}</span><span className="hot-name">{stock.name}</span></div>
                  <div className="hot-stock-stats"><span className="hot-volume">{stock.volume}</span><span className="hot-traders">{stock.traders} traders</span></div>
                  <span className={`hot-change ${parseFloat(stock.change) >= 0 ? 'positive' : 'negative'}`}>{stock.change}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="active-traders-section">
        <div className="section-header"><h2><Users size={22} /> Most Active Congressional Traders - Last Year</h2><span className="section-hint">Click on a politician for more details</span></div>
        <div className="active-traders-table-wrapper">
          <table className="active-traders-table">
            <thead><tr><th>Name</th><th>Trades</th><th>Estimated Trade Volume</th></tr></thead>
            <tbody>
              {[
                { name: 'Michael T. McCaul', party: 'R', chamber: 'House', trades: 1013, volume: '$57,941,000' },
                { name: 'Ro Khanna', party: 'D', chamber: 'House', trades: 4130, volume: '$53,561,500' },
                { name: 'Nancy Pelosi', party: 'D', chamber: 'House', trades: 19, volume: '$51,700,000' },
                { name: 'Jefferson Shreve', party: 'R', chamber: 'House', trades: 556, volume: '$29,327,000' },
                { name: 'Cleo Fields', party: 'D', chamber: 'House', trades: 205, volume: '$21,509,500' },
                { name: 'Josh Gottheimer', party: 'D', chamber: 'House', trades: 316, volume: '$19,504,000' },
                { name: 'Marjorie Taylor Greene', party: 'R', chamber: 'House', trades: 213, volume: '$1,949,000' },
                { name: 'Tommy Tuberville', party: 'R', chamber: 'Senate', trades: 26, volume: '$881,000' },
              ].map((trader) => (
                <tr key={trader.name} className="trader-row" onClick={() => handleViewPolitician(trader.name)}>
                  <td className="trader-name-cell"><div className="trader-avatar"><span className={`party-indicator ${trader.party === 'D' ? 'dem' : 'rep'}`}></span></div><div className="trader-info"><span className="trader-name">{trader.name}</span><span className="trader-meta">{trader.chamber} - {trader.party === 'D' ? 'Democratic' : 'Republican'}</span></div></td>
                  <td className="trader-trades">{trader.trades.toLocaleString()}</td>
                  <td className="trader-volume">{trader.volume}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="networth-section">
        <div className="section-header"><h2><DollarSign size={22} /> Highest Net Worth Congressmembers</h2><span className="section-hint">Click on a politician for net worth details</span></div>
        <div className="networth-table-wrapper">
          <table className="networth-table">
            <thead><tr><th>#</th><th>Politician</th><th>Current Net Worth</th></tr></thead>
            <tbody>
              {[
                { rank: 1, name: 'Rick Scott', party: 'R', chamber: 'Senate', netWorth: '$548.2M' },
                { rank: 2, name: 'Mark Warner', party: 'D', chamber: 'Senate', netWorth: '$214.1M' },
                { rank: 3, name: 'Vern Buchanan', party: 'R', chamber: 'House', netWorth: '$157.3M' },
                { rank: 4, name: 'John Hoeven', party: 'R', chamber: 'Senate', netWorth: '$93.7M' },
                { rank: 5, name: 'Mitt Romney', party: 'R', chamber: 'Senate', netWorth: '$84.6M' },
                { rank: 6, name: 'Daniel Meuser', party: 'R', chamber: 'House', netWorth: '$69.1M' },
              ].map((person) => (
                <tr key={person.name} className="networth-row" onClick={() => handleViewPolitician(person.name)}>
                  <td className="rank-cell">{person.rank}</td>
                  <td className="politician-name-cell"><div className="politician-avatar"><span className={`party-indicator ${person.party === 'D' ? 'dem' : 'rep'}`}></span></div><div className="politician-info"><span className="politician-name">{person.name}</span><span className="politician-meta">{person.chamber} - {person.party === 'D' ? 'Democratic' : 'Republican'}</span></div></td>
                  <td className="networth-cell">{person.netWorth}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="networth-disclaimer">* Note: These are rough estimates. Data may be inaccurate or incomplete. Does not include primary residence or outstanding liabilities.</p>
      </div>

      <div className="sidebar">
        <div className="sidebar-card performance-card">
          <div className="card-header"><h3><BarChart3 size={18} /> Strategy Performance</h3><span className="time-badge">1Y</span></div>
          <div className="performance-chart">
            <div className="chart-placeholder">
              <svg viewBox="0 0 300 120" className="mini-chart">
                <defs><linearGradient id="congressGrad" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#667eea" stopOpacity="0.3"/><stop offset="100%" stopColor="#667eea" stopOpacity="0"/></linearGradient></defs>
                <path d="M0,80 Q50,70 100,50 T200,30 T300,10" fill="none" stroke="#667eea" strokeWidth="2"/>
                <path d="M0,80 Q50,70 100,50 T200,30 T300,10 L300,120 L0,120 Z" fill="url(#congressGrad)"/>
                <path d="M0,90 Q50,85 100,80 T200,70 T300,60" fill="none" stroke="#6b7280" strokeWidth="1" strokeDasharray="4"/>
              </svg>
            </div>
            <div className="performance-legend">
              <div className="legend-item"><span className="legend-dot congress"></span><span>Congress Strategy</span><span className="legend-value positive">+34.2%</span></div>
              <div className="legend-item"><span className="legend-dot sp500"></span><span>S&P 500</span><span className="legend-value">+12.1%</span></div>
            </div>
          </div>
          <div className="performance-stats">
            <div className="perf-stat"><span className="perf-label">Sharpe Ratio</span><span className="perf-value">1.42</span></div>
            <div className="perf-stat"><span className="perf-label">Win Rate</span><span className="perf-value">67%</span></div>
            <div className="perf-stat"><span className="perf-label">Max Drawdown</span><span className="perf-value">-12.3%</span></div>
          </div>
        </div>

        <div className="sidebar-card leaderboard-card">
          <div className="card-header"><h3><Award size={18} /> Top Traders (1Y)</h3><button className="see-all-btn">See All <ChevronRight size={14} /></button></div>
          <div className="leaderboard">{topTraders.map((trader, idx) => (<LeaderboardRow key={trader.name} trader={trader} rank={idx + 1} onClick={handleViewPolitician} />))}</div>
        </div>

        <div className="sidebar-card party-card">
          <div className="card-header"><h3><Users size={18} /> Party Breakdown (30d)</h3></div>
          <div className="party-comparison">
            <div className="party-row democrat"><div className="party-label"><span className="party-dot dem"></span><span>Democrats</span></div><div className="party-stats"><span className="buys">{partyStats.democrat.buys} buys</span><span className="sells">{partyStats.democrat.sells} sells</span></div><div className="party-bar"><div className="bar-fill dem" style={{ width: '73%' }}></div></div></div>
            <div className="party-row republican"><div className="party-label"><span className="party-dot rep"></span><span>Republicans</span></div><div className="party-stats"><span className="buys">{partyStats.republican.buys} buys</span><span className="sells">{partyStats.republican.sells} sells</span></div><div className="party-bar"><div className="bar-fill rep" style={{ width: '76%' }}></div></div></div>
          </div>
          <div className="sector-tags"><span className="sector-tag dem">Dem Top: Technology</span><span className="sector-tag rep">Rep Top: Defense</span></div>
        </div>

        <div className="sidebar-card convergence-card">
          <div className="card-header"><h3><Zap size={18} /> Convergence Signals</h3><span className="signal-count">{convergenceSignals.length} Active</span></div>
          <p className="convergence-desc">Stocks where Congress, Insiders & Institutions are aligned</p>
          <div className="convergence-list">{convergenceSignals.slice(0, 4).map(signal => (<ConvergenceItem key={signal.symbol} signal={signal} onClick={handleViewStock} />))}</div>
        </div>
      </div>

      <AlertModal isOpen={showAlertModal} onClose={() => setShowAlertModal(false)} />
      <ExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} trades={filteredTrades} />
    </div>
  );
}

export default CongressPage;